<script setup>
import dayjs from 'dayjs'
import { ref, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { useClone } from '@/composables/object'
import { useNavigator } from '@/composables/navigator'
import { useDetectDevice } from '@/composables/device'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  company: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()

const form = ref(null)
const editItem = ref({})
const contacts = ref([])
const showDeleteDialog = ref(false)
const isUpdating = ref(false)

const conversationTypeOptions = portalStore.getLocalizedConversationTypes()

const emit = defineEmits(['update', 'cancel'])
useNavigator(() => emit('cancel'))

onMounted(async() => {
  editItem.value = useClone(props.modelValue)
  editItem.value.occurred_at = dayjs(editItem.value.occurred_at).format('YYYY-MM-DDTHH:mm')
  const { data } = await planningStore.fetchContactsByCompanyAsync(props.company.id)
  contacts.value = data
})

const onSave = async() => {
  const valid = await form.value.validate()
  if (!valid) return
  isUpdating.value = true
  try {
    await planningStore.upsertConversationAsync({
      ...editItem.value,
      occurred_at: dayjs(editItem.value.occurred_at).toISOString(),
    })
    notifySuccess(editItem.value.id ? i18n.t('info.updatedSuccessfully') : i18n.t('info.createdSuccessfully'))
    emit('update')
  } finally {
    isUpdating.value = false
  }
}

const onDelete = async() => {
  await planningStore.deleteConversationAsync(editItem.value.id)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
  emit('update')
}
</script>

<template>
  <q-card v-drag-dialog="!isMobile" class="dialog-card-flex">
    <confirm-dialog
      v-model="showDeleteDialog"
      :message="i18n.t('common.confirmDelete')"
      icon="mdi-delete-outline"
      icon-color="negative"
      @confirm="onDelete"
    />
    <navigate-back
      :text="editItem?.id ? i18n.t('common.activity') : i18n.t('company.logActivity')"
      @cancel="emit('cancel')"
    />
    <q-card-section class="scroll col q-pa-sm">
      <q-form ref="form">
        <div class="row q-col-gutter-sm">
          <q-select
            v-model="editItem.conversation_type"
            :class="isMobile ? 'col-12' : 'col-4'"
            :label="i18n.t('common.type')"
            :options="conversationTypeOptions"
            option-value="id"
            option-label="name"
            emit-value
            map-options
          />
          <q-input
            v-model="editItem.occurred_at"
            :class="isMobile ? 'col-12' : 'col-4'"
            :label="i18n.t('common.date')"
            type="datetime-local"
            :rules="[val => !!val || i18n.t('common.required')]"
          />
          <q-select
            v-model="editItem.contact"
            :class="isMobile ? 'col-12' : 'col-4'"
            :label="i18n.t('common.contact')"
            :options="contacts"
            option-value="id"
            option-label="name"
            emit-value
            map-options
            clearable
          />
          <q-input
            v-model="editItem.note"
            class="col-12"
            :label="i18n.t('common.note')"
            autogrow
          />
        </div>
      </q-form>
    </q-card-section>
    <q-card-actions align="right" class="q-pa-md">
      <q-btn
        v-if="editItem?.id"
        class="q-mr-sm"
        flat
        color="negative"
        :label="i18n.t('common.delete')"
        @click="showDeleteDialog = true"
      />
      <q-btn
        color="primary"
        :label="editItem?.id ? i18n.t('common.update') : i18n.t('common.add')"
        :loading="isUpdating"
        @click="onSave"
      />
    </q-card-actions>
  </q-card>
</template>
